import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchOrdersPaginated, createOrdersCall, lockOrder, unlockOrder, updateAutocallOrder, bulkUpdateOrderWindow, type BulkWindowSkipReason } from "~/features/orders/api";
import { ApiError } from "~/shared/utils/api-error";
import { configureMsalInstance, isTimeoutError } from "~/services/api-client";
import { useColumnFilters } from "~/shared/hooks/useColumnFilters";
import { useTokenErrorHandler } from "~/shared/hooks/useTokenErrorHandler";
import { useServerSearch } from "~/shared/hooks/useServerSearch";
import { usePersistedFilter } from "~/shared/hooks/usePersistedFilter";
import type { Step, Toast } from "~/features/orders/types";
import type { FiltersContextual } from "~/features/orders/types-interfaces";
import { workflowFilterColumns, CALL_FLOW_TO_API_TYPE, type CallFlowDisplayName } from "~/features/orders/constants";
import type { WindowBucket } from "~/features/orders/types-order";
import { RxStatus } from "~/features/orders/types-order";
import { Step1Select } from "~/features/orders/components/Step1Select";
import type { AutocallOrderEditUpdates } from "~/features/orders/components/AutocallOrderEditModal";
import { formatWindowRange, type DeliveryWindowPreset, type WindowMinutes } from "~/shared/utils/delivery-window";
import { Toast as ToastComponent } from "~/shared/components/Toast";
import { CenteredLoading } from "~/shared/components/CenteredLoading";
import { ErrorState } from "~/shared/components/ErrorState";
import { useLocationCode } from "~/shared/contexts/LocationCodeContext";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { useOrderWebSocket, type OrderLockEvent } from "~/features/orders/hooks";
import { loginRequest } from "~/services/auth-service";

const BULK_SKIP_REASON_TEXT: Record<BulkWindowSkipReason, string> = {
  not_found: "not found",
  no_location_access: "no location access",
  call_in_progress: "call in progress",
  locked_by_another_user: "locked by another dispatcher",
  update_failed: "update failed",
};

/** Releases a lock without surfacing errors to the user (only logged); the lease expires on its own anyway. */
function unlockOrderQuietly(smNumber: string): Promise<void> {
  return unlockOrder(smNumber).then(
    () => undefined,
    (err: unknown) => console.error(`Failed to unlock order ${smNumber}:`, err)
  );
}

function describeSkipReason(reason: string): string {
  return (BULK_SKIP_REASON_TEXT as Record<string, string>)[reason] ?? reason;
}

function isAbortOrCancel(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  if ("name" in err && (err as Error).name === "AbortError") return true;
  if ("message" in err && String((err as Error).message).includes("cancel")) return true;
  return false;
}

function getOrdersLoadErrorMessage(err: unknown): string {
  if (isTimeoutError(err)) {
    return "Request took too long. The server may be waking up after inactivity. Please try again.";
  }
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Failed to load orders";
}

const AUTOCALL_UI_STORAGE_KEY = "app:autocall_ui";
const VALID_DATE_FILTERS = ["today", "future", "all"] as const;
function loadAutocallUi(): { dateFilter: 'today' | 'future' | 'all'; page: number } {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(AUTOCALL_UI_STORAGE_KEY) : null;
    if (!raw) return { dateFilter: "all", page: 1 };
    const parsed = JSON.parse(raw) as { dateFilter?: string; page?: number };
    const dateFilter = parsed.dateFilter && VALID_DATE_FILTERS.includes(parsed.dateFilter as typeof VALID_DATE_FILTERS[number])
      ? (parsed.dateFilter as "today" | "future" | "all")
      : "all";
    const page = typeof parsed.page === "number" && parsed.page >= 1 ? Math.floor(parsed.page) : 1;
    return { dateFilter, page };
  } catch {
    return { dateFilter: "all", page: 1 };
  }
}

export default function AutoCallPage() {
  const { instance: msalInstance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [step] = useState<Step>(1);
  type Order = Awaited<ReturnType<typeof fetchOrdersPaginated>>['orders'][number];
  type CallBlock = { id: number; order_ids: string[]; time_window: { start: number; end: number }; status: string; created_at: number };

  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const;

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(() => loadAutocallUi().page);
  const [pageSize, setPageSize] = useState(200);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [toast, setToast] = useState<Toast>(null);
  const { selectedLocationCode } = useLocationCode();
  const selectedLocationCodeRef = useRef(selectedLocationCode);
  const { jobLocations } = useDeliveryWindows();
  const hasLoadedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const currentUserIdRef = useRef("");
  const ordersRef = useRef<Order[]>([]);
  const lockTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const callInProgressTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const mountedRef = useRef(true);
  const unlockingRef = useRef(false);
  // Orders locked only for the edit side panel (not selected); released when the panel closes
  const editLocksRef = useRef<Set<string>>(new Set());

  // Server-side column filters — sent as API query params, persisted to localStorage
  type ColumnServerFilters = { cities: string[]; dates: string[]; windows: string[] };
  const [acCities, setAcCities, acCitiesRef] = usePersistedFilter('ac:cities');
  const [acDates, setAcDates, acDatesRef] = usePersistedFilter('ac:dates');
  const [acWindows, setAcWindows, acWindowsRef] = usePersistedFilter('ac:windows');
  const columnFilters: ColumnServerFilters = { cities: acCities, dates: acDates, windows: acWindows };
  const columnFiltersRef = useRef<ColumnServerFilters>({ cities: acCitiesRef.current, dates: acDatesRef.current, windows: acWindowsRef.current });
  const [filtersContextual, setFiltersContextual] = useState<FiltersContextual>({ cities: [], dates: [], windows: [], locationCodes: [] });
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);

  const { handleTokenError, resetTokenErrorRetry } = useTokenErrorHandler(msalInstance);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const currentUserId = useMemo(() => {
    const email = accounts[0]?.username ?? '';
    const prefix = email.split('@')[0]?.toUpperCase() ?? '';
    return prefix ? `BAYSHORE\\${prefix}` : '';
  }, [accounts]);

  // Keep currentUserIdRef in sync
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  // Keep ordersRef in sync for timer callbacks
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  // --- Auto-unlock timer management ---
  const clearLockTimer = useCallback((smNumber: string) => {
    const timer = lockTimersRef.current.get(smNumber);
    if (timer) {
      clearTimeout(timer);
      lockTimersRef.current.delete(smNumber);
    }
  }, []);

  const autoUnlockOrder = useCallback((smNumber: string) => {
    console.log('[Autocall] Auto-unlock: lease expired for order', smNumber);
    setOrders(prev =>
      prev.map(order =>
        order.sm_number === smNumber
          ? { ...order, isBlocked: false, isBlockedBy: '' }
          : order
      )
    );
    const order = ordersRef.current.find(o => o.sm_number === smNumber);
    if (order) {
      setSelected(prev => {
        if (prev[order.id]) {
          const updated = { ...prev };
          delete updated[order.id];
          return updated;
        }
        return prev;
      });
    }
  }, []);

  const scheduleLockExpiry = useCallback((smNumber: string, expiresAt: string) => {
    clearLockTimer(smNumber);
    const delay = new Date(expiresAt).getTime() - Date.now();
    if (delay <= 0) {
      autoUnlockOrder(smNumber);
      return;
    }
    const timer = setTimeout(() => {
      lockTimersRef.current.delete(smNumber);
      autoUnlockOrder(smNumber);
    }, delay);
    lockTimersRef.current.set(smNumber, timer);
  }, [clearLockTimer, autoUnlockOrder]);

  // Clean up all lock timers on unmount
  useEffect(() => {
    return () => {
      lockTimersRef.current.forEach(timer => clearTimeout(timer));
      lockTimersRef.current.clear();
    };
  }, []);

  // Leaving the page with the edit panel open: release the lock taken for it
  useEffect(() => {
    const editLocks = editLocksRef.current;
    return () => {
      editLocks.forEach(unlockOrderQuietly);
      editLocks.clear();
    };
  }, []);

  // --- Call-in-progress auto-clear timer management ---
  // Orders with rxStatus "call-in-progress" are removed from the list after 5 minutes
  // (based on updatedAt), keeping the screen fresh for dispatchers.
  const CALL_IN_PROGRESS_TTL_MS = 5 * 60 * 1000; // 5 minutes

  const clearCallInProgressTimer = useCallback((smNumber: string) => {
    const timer = callInProgressTimersRef.current.get(smNumber);
    if (timer) {
      clearTimeout(timer);
      callInProgressTimersRef.current.delete(smNumber);
    }
  }, []);

  const removeCallInProgressOrder = useCallback((smNumber: string) => {
    console.log('[Autocall] Auto-clear: call-in-progress expired for order', smNumber);
    setOrders(prev => prev.filter(order => order.sm_number !== smNumber));
  }, []);

  const scheduleCallInProgressExpiry = useCallback((smNumber: string, updatedAt: string) => {
    clearCallInProgressTimer(smNumber);
    const delay = new Date(updatedAt).getTime() + CALL_IN_PROGRESS_TTL_MS - Date.now();
    if (delay <= 0) {
      removeCallInProgressOrder(smNumber);
      return;
    }
    const timer = setTimeout(() => {
      callInProgressTimersRef.current.delete(smNumber);
      removeCallInProgressOrder(smNumber);
    }, delay);
    callInProgressTimersRef.current.set(smNumber, timer);
  }, [clearCallInProgressTimer, removeCallInProgressOrder]);

  // Clean up all call-in-progress timers on unmount
  useEffect(() => {
    return () => {
      callInProgressTimersRef.current.forEach(timer => clearTimeout(timer));
      callInProgressTimersRef.current.clear();
    };
  }, []);

  // Handle order_locked / order_unlocked events from other dispatchers via WebSocket
  const handleWsOrderLockEvent = useCallback((event: OrderLockEvent) => {
    console.log('[Autocall] WS order lock event:', event.eventType, event.orderId, event.lockedBy);

    setOrders(prev =>
      prev.map(order => {
        if (order.sm_number === event.orderId) {
          if (event.eventType === 'order_locked') {
            return { ...order, isBlocked: true, isBlockedBy: event.lockedBy };
          } else if (event.eventType === 'order_unlocked') {
            return { ...order, isBlocked: false, isBlockedBy: '' };
          }
        }
        return order;
      })
    );

    // Schedule or clear auto-unlock timer based on event type
    if (event.eventType === 'order_locked' && event.leaseExpiresAt) {
      scheduleLockExpiry(event.orderId, event.leaseExpiresAt);
    } else if (event.eventType === 'order_unlocked') {
      clearLockTimer(event.orderId);
    }

    // If another dispatcher locked an order that the current user had selected, deselect it
    if (event.eventType === 'order_locked' && event.lockedBy !== currentUserId) {
      setSelected(prev => {
        const order = orders.find(o => o.sm_number === event.orderId);
        if (order && prev[order.id]) {
          const updated = { ...prev };
          delete updated[order.id];
          return updated;
        }
        return prev;
      });
    }
  }, [currentUserId, orders, scheduleLockExpiry, clearLockTimer]);

  useOrderWebSocket({
    userId: currentUserId,
    jobLocations,
    onOrderLockEvent: handleWsOrderLockEvent,
    enabled: true,
  });

  const load = useCallback(async (reqPage?: number, reqLimit?: number) => {
    if (isLoadingRef.current) return;
    const p = reqPage ?? pageRef.current;
    const lim = reqLimit ?? pageSizeRef.current;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const cf = columnFiltersRef.current;
      const loc = selectedLocationCodeRef.current;
      const { orders: apiOrders, pagination: pag, filtersContextual: fc } = await fetchOrdersPaginated({
        page: p,
        limit: lim,
        search: searchQueryRef.current ?? undefined,
        cities: cf.cities.length > 0 ? cf.cities : undefined,
        dates: cf.dates.length > 0 ? cf.dates : undefined,
        windows: cf.windows.length > 0 ? cf.windows : undefined,
        locationCodes: loc ? [loc] : undefined,
      });
      setOrders(apiOrders);
      setPagination(pag);
      setPage(pag.page);
      setFiltersContextual(fc);
      setError(null);
      resetTokenErrorRetry();

      // Replace selection state entirely based on backend truth.
      // Only orders currently locked by the current user should be selected.
      // Skip if an unlock operation is in progress to avoid re-selecting orders mid-unlock.
      if (!unlockingRef.current) {
        const selfLockedIds: Record<string, boolean> = {};
        const uid = currentUserIdRef.current;
        apiOrders.forEach(order => {
          // An order locked only for the open edit panel is not a selection
          if (editLocksRef.current.has(order.sm_number)) return;
          if (order.isBlocked && order.isBlockedBy && uid && order.isBlockedBy.toUpperCase() === uid.toUpperCase()) {
            selfLockedIds[order.id] = true;
          }
        });
        setSelected(selfLockedIds);
      }

      // Re-schedule auto-unlock timers for any orders with active locks
      apiOrders.forEach(order => {
        if (order.isBlocked && order.lockExpiresAt) {
          scheduleLockExpiry(order.sm_number, order.lockExpiresAt);
        }
      });

      // Schedule auto-clear timers for call-in-progress orders
      apiOrders.forEach(order => {
        if (order.rxStatus === RxStatus.CALL_IN_PROGRESS && order.updatedAt) {
          scheduleCallInProgressExpiry(order.sm_number, order.updatedAt);
        }
      });

      hasLoadedRef.current = true;
    } catch (err: unknown) {
      if (isAbortOrCancel(err)) return;
      if (handleTokenError(err, () => load(p, lim), () => { isLoadingRef.current = false; })) return;

      const errorMessage = getOrdersLoadErrorMessage(err);
      setError(errorMessage);
      setToast({ message: errorMessage, type: "error" });
      setTimeout(() => setToast(null), 5000);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
      abortControllerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTokenError, resetTokenErrorRetry]);

  // Keep refs in sync for use inside load()
  useEffect(() => { columnFiltersRef.current = columnFilters; }, [columnFilters]);
  useEffect(() => { selectedLocationCodeRef.current = selectedLocationCode; }, [selectedLocationCode]);

  // Ref to always hold the latest load function for filter/search triggers
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // Server-side search (debounced input + immediate Enter)
  const { searchQuery, searchQueryRef, handleSearchChange, handleSearchSubmit } = useServerSearch({
    onSearch: () => loadRef.current(1, pageSize),
  });

  // Handle column filter change (date/city/window) — triggers API call immediately
  const handleColumnFilterChange = useCallback((key: keyof ColumnServerFilters, selected: string[]) => {
    if (key === 'cities') { setAcCities(selected); acCitiesRef.current = selected; }
    else if (key === 'dates') { setAcDates(selected); acDatesRef.current = selected; }
    else if (key === 'windows') { setAcWindows(selected); acWindowsRef.current = selected; }
    columnFiltersRef.current = { ...columnFiltersRef.current, [key]: selected };
    loadRef.current(1, pageSizeRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAcCities, setAcDates, setAcWindows]);

  useEffect(() => {
    configureMsalInstance(msalInstance);
    // Always fetch on mount (component remounts on navigation)
    hasLoadedRef.current = false;
    setSelected({});
    load(1, pageSize);

    const state = location.state as { error?: string } | null;
    if (state?.error) {
      setToast({ message: state.error, type: 'error' });
      setTimeout(() => setToast(null), 5000);
      setTimeout(() => navigate('/autocall', { replace: true, state: {} }), 0);
    }

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Keep page/pageSize refs in sync
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { pageSizeRef.current = pageSize; }, [pageSize]);

  // Refetch when user changes page or page size (skip on initial mount; initial load is done above)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!hasLoadedRef.current) return;
    load(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // When user returns to the tab after long idle: refresh token then refetch so the request uses a valid token
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (!hasLoadedRef.current || isLoadingRef.current || !accounts?.[0]) return;

      const account = accounts[0];
      msalInstance
        .acquireTokenSilent({ ...loginRequest, account, forceRefresh: true })
        .then(() => {
          if (hasLoadedRef.current && !isLoadingRef.current) {
            load(page, pageSize);
          }
        })
        .catch(() => {
          // Silent refresh failed (e.g. session expired); leave as-is, next request may get 401 and redirect
        });
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [page, pageSize, load, msalInstance, accounts]);


  // Re-fetch orders when location changes (server-side filtering)
  const prevLocationRef = useRef(selectedLocationCode);
  useEffect(() => {
    if (prevLocationRef.current === selectedLocationCode) return;
    prevLocationRef.current = selectedLocationCode;
    if (!hasLoadedRef.current) return;
    loadRef.current(1, pageSizeRef.current);
  }, [selectedLocationCode]);

  const {
    filteredRows: filteredOrders,
    uniqueValues: orderFilterValues,
    filters: orderFilters,
    selectOnly: selectOrderFilterOnly,
    clearFilter: clearOrderFilter,
    clearAllFilters: clearAllOrderFilters,
  } = useColumnFilters(orders, workflowFilterColumns, { persistKey: "autocall" });

  // Use local date so "today" matches the user's calendar day (e.g. Canada March 12 vs India March 13), not UTC.
  const todayKey = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  // Date filter: which orders to list (today / future / all). Buttons filter the list; they do not lock orders.
  const [dateFilter, setDateFilter] = useState<'today' | 'future' | 'all'>(() => loadAutocallUi().dateFilter);
  const todayOrders = useMemo(
    () => filteredOrders.filter(o => o.delivery_date === todayKey || o.requested_delivery_date === todayKey),
    [filteredOrders, todayKey]
  );
  const futureOrders = useMemo(
    () => filteredOrders.filter(o => (o.requested_delivery_date ?? o.delivery_date ?? '') > todayKey),
    [filteredOrders, todayKey]
  );
  const displayedOrders = useMemo(() => {
    if (dateFilter === 'today') return todayOrders;
    if (dateFilter === 'future') return futureOrders;
    return filteredOrders;
  }, [dateFilter, todayOrders, futureOrders, filteredOrders]);
  const visibleSelectedIds = useMemo(
    () => displayedOrders.filter(order => selected[order.id]).map(order => order.id),
    [displayedOrders, selected]
  );

  useEffect(() => {
    try {
      localStorage.setItem(AUTOCALL_UI_STORAGE_KEY, JSON.stringify({ dateFilter, page }));
    } catch {
      // ignore
    }
  }, [dateFilter, page, pageSize]);


  // Helper: get SM numbers from internal order IDs
  const getSmNumbers = useCallback((ids: string[]): string[] => {
    return ids
      .map(id => orders.find(o => o.id === id)?.sm_number)
      .filter((sm): sm is string => !!sm);
  }, [orders]);

  // Helper: lock a single order. Shows toast on error and rethrows so caller can revert UI.
  const callLockOrder = useCallback(async (smNumber: string): Promise<void> => {
    // Already locked by this user for the edit panel: the selection takes that lock over
    if (editLocksRef.current.delete(smNumber)) return;
    try {
      const response = await lockOrder(smNumber);
      const expiresAt = response.data?.lockStatus?.expiresAt;
      if (expiresAt) {
        scheduleLockExpiry(smNumber, expiresAt);
      }
    } catch (err) {
      console.error(`Failed to lock order ${smNumber}:`, err);
      const msg = err instanceof Error ? err.message : 'Failed to lock order';
      setToast({ message: msg, type: 'error' });
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  }, [scheduleLockExpiry]);

  // Helper: unlock a single order. Shows toast on error and rethrows so caller can revert UI.
  const callUnlockOrder = useCallback(async (smNumber: string): Promise<void> => {
    try {
      clearLockTimer(smNumber);
      await unlockOrder(smNumber);
    } catch (err) {
      console.error(`Failed to unlock order ${smNumber}:`, err);
      const msg = err instanceof Error ? err.message : 'Failed to unlock order';
      setToast({ message: msg, type: 'error' });
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  }, [clearLockTimer]);

  // Helper: check if an order is blocked by another dispatcher
  const isBlockedByOther = useCallback((order: Order) => {
    return order.isBlocked && order.isBlockedBy !== currentUserId && !!order.isBlockedBy;
  }, [currentUserId]);

  // Helper: check if an order is currently being called (cannot be selected)
  const isCallInProgress = useCallback((order: Order) => {
    return order.rxStatus === RxStatus.CALL_IN_PROGRESS;
  }, []);

  // Selectable = not blocked by another user and not call-in-progress
  const selectableOrders = displayedOrders.filter(o => !isCallInProgress(o) && !isBlockedByOther(o));
  const allSelected = selectableOrders.length > 0 && selectableOrders.every(o => selected[o.id]);

  async function selectMap(ids: string[]) {
    const selectableIds = ids.filter(id => {
      const order = orders.find(o => o.id === id);
      return order && !isBlockedByOther(order) && !isCallInProgress(order);
    });

    const prevSelectedIds = Object.keys(selected).filter(id => selected[id]);
    const idsToUnlock = prevSelectedIds.filter(id => !selectableIds.includes(id));
    const idsToLock = selectableIds.filter(id => !prevSelectedIds.includes(id));

    const map: Record<string, boolean> = {};
    selectableIds.forEach(id => { map[id] = true; });
    setSelected(map);

    const ordersToUnlock = idsToUnlock
      .map(id => orders.find(o => o.id === id))
      .filter((o): o is Order => !!o);
    const ordersToLock = idsToLock
      .map(id => orders.find(o => o.id === id))
      .filter((o): o is Order => !!o);

    if (ordersToUnlock.length > 0) {
      unlockingRef.current = true;
      ordersToUnlock.forEach(o => clearLockTimer(o.sm_number));
      await Promise.allSettled(
        ordersToUnlock.map(o =>
          unlockOrder(o.sm_number).catch(err => {
            console.error(`Failed to unlock order ${o.sm_number}:`, err);
          })
        )
      );
      unlockingRef.current = false;
    }

    const lockResults = await Promise.allSettled(
      ordersToLock.map(async (o) => {
        try {
          await callLockOrder(o.sm_number);
          return { id: o.id, ok: true };
        } catch {
          return { id: o.id, ok: false };
        }
      })
    );

    if (!mountedRef.current) return;
    const failedIds = lockResults
      .filter(r => r.status === 'fulfilled' && !r.value.ok)
      .map(r => (r as PromiseFulfilledResult<{ id: string; ok: boolean }>).value.id);
    if (failedIds.length > 0) {
      setSelected(s => {
        const next = { ...s };
        failedIds.forEach(id => delete next[id]);
        return next;
      });
    }
  }

  // Filter the list by date (toggle: same button again deselects).
  function toggleDateFilter(mode: 'today' | 'future') {
    setDateFilter((prev) => (prev === mode ? 'all' : mode));
  }

  async function clearSelection() {
    const prevSelectedIds = Object.keys(selected).filter(id => selected[id]);
    const ordersToUnlock = prevSelectedIds
      .map(id => orders.find(o => o.id === id))
      .filter((o): o is Order => !!o);

    setSelected({});
    if (ordersToUnlock.length === 0) return;

    unlockingRef.current = true;
    ordersToUnlock.forEach(o => clearLockTimer(o.sm_number));

    await Promise.allSettled(
      ordersToUnlock.map(o =>
        unlockOrder(o.sm_number).catch(err => {
          console.error(`Failed to unlock order ${o.sm_number}:`, err);
        })
      )
    );

    unlockingRef.current = false;
    // After all unlocks finish, if still mounted, refetch to sync UI with backend
    if (mountedRef.current) {
      hasLoadedRef.current = false;
      load(page, pageSize);
    }
  }

  function toggleAll() {
    if (allSelected) {
      clearSelection();
    } else {
      selectMap(displayedOrders.map(o => o.id));
    }
  }

  async function deselectOrder(id: string, smNumber: string) {
    setSelected(s => {
      const updated = { ...s };
      delete updated[id];
      return updated;
    });
    try {
      await callUnlockOrder(smNumber);
    } catch {
      if (mountedRef.current) setSelected(s => ({ ...s, [id]: true }));
    }
  }

  async function selectOrder(id: string, smNumber: string) {
    setSelected(s => ({ ...s, [id]: true }));
    try {
      await callLockOrder(smNumber);
    } catch {
      if (mountedRef.current) {
        setSelected(s => {
          const next = { ...s };
          delete next[id];
          return next;
        });
      }
    }
  }

  async function toggle(id: string) {
    const order = orders.find(o => o.id === id);
    if (!order) return;
    if (isBlockedByOther(order) && !selected[id]) return;
    if (isCallInProgress(order)) return;

    if (selected[id]) {
      await deselectOrder(id, order.sm_number);
    } else {
      await selectOrder(id, order.sm_number);
    }
  }

  async function createAndStart(flow: CallFlowDisplayName) {
    // Only consider orders that are visible in the current view (date/filter/location/page)
    const visibleSelectedIds = displayedOrders
      .filter(o => selected[o.id])
      .map(o => o.id);

    if (visibleSelectedIds.length === 0) {
      setToast({ message: 'Select at least one order', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    const selectedOrders = orders.filter(o => visibleSelectedIds.includes(o.id));
    const orderSmNumbers = selectedOrders.map(o => o.sm_number);
    const callFlowType = CALL_FLOW_TO_API_TYPE[flow];
    const first = selectedOrders[0];
    const win = first?.preferred_window ?? { start: 11, end: 17 };

    const demoBlock: CallBlock = {
      id: Date.now(),
      order_ids: visibleSelectedIds,
      time_window: win,
      status: 'calling',
      created_at: Date.now(),
    };

    navigate('/transfer-queue', {
      state: {
        block: demoBlock,
        orders: orders,
        selectedIds: visibleSelectedIds,
        flow,
      },
    });

    createOrdersCall({ orderIds: orderSmNumbers, callFlowType })
      .then((callResponse) => {
        console.log('[Autocall] Orders call started:', callResponse);
      })
      .catch((apiErr: unknown) => {
        console.error('[Autocall] Failed to start orders call:', apiErr);
        const msg = apiErr instanceof ApiError ? apiErr.message : 'Failed to start call';
        setToast({ message: msg, type: 'error' });
        setTimeout(() => setToast(null), 5000);
      });
  }

  /** Window as stored by the backend (hours + minutes + derived label); local fallback if the response omits it. */
  function toStoredWindow(saved: WindowBucket | undefined, window: WindowMinutes): WindowBucket {
    return saved ?? {
      start: Math.floor(window.startMinutes / 60),
      end: Math.ceil(window.endMinutes / 60),
      label: '',
      ...window,
    };
  }

  function showToast(message: string, type: NonNullable<Toast>['type'], ms: number) {
    setToast({ message, type });
    setTimeout(() => setToast(null), ms);
  }

  function errorMessage(err: unknown, fallback: string): string {
    return err instanceof ApiError || err instanceof Error ? err.message : fallback;
  }

  async function saveOrderEdits(order: Order, updates: AutocallOrderEditUpdates) {
    try {
      configureMsalInstance(msalInstance);

      // Append Z to treat the date/time strings as UTC, preventing local-timezone
      // offset from shifting the date when the dispatcher is in a UTC+ timezone.
      const updatedDeliveryDate = new Date(`${updates.deliveryDate}T00:00:00.000Z`).toISOString();
      const updatedTimeRequired = new Date(`${updates.deliveryDate}T${updates.timeRequired}:00.000Z`).toISOString();
      const saved = await updateAutocallOrder(order.sm_number, {
        updatedDeliveryDate,
        updatedTimeRequired,
        updatedWindow: updates.window,
      });

      // Keep UI in sync without waiting for next refresh.
      setOrders(prev => prev.map(o => {
        if (o.id !== order.id) return o;
        return {
          ...o,
          requested_delivery_date: updates.deliveryDate,
          preferred_window: toStoredWindow(saved?.updatedWindow, updates.window),
          time_required: updates.timeRequired,
        };
      }));

      showToast("Order updated successfully", "success", 3000);
    } catch (err) {
      showToast(errorMessage(err, "Failed to update order"), "error", 5000);
    }
  }

  /** Grid Window cell: saves only the window (date and time required are untouched). */
  async function saveOrderWindow(order: Order, preset: DeliveryWindowPreset) {
    const window: WindowMinutes = { startMinutes: preset.startMinutes, endMinutes: preset.endMinutes };
    try {
      configureMsalInstance(msalInstance);
      const saved = await updateAutocallOrder(order.sm_number, { updatedWindow: window });
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, preferred_window: toStoredWindow(saved?.updatedWindow, window) } : o
      ));
      showToast(`Delivery window updated for ${order.sm_number} · ${formatWindowRange(preset)}`, "success", 3000);
    } catch (err) {
      showToast(errorMessage(err, "Failed to update delivery window"), "error", 5000);
    }
  }

  /**
   * Edit side panel opened: lock the order so no one else can change it meanwhile.
   * An order the user already selected is already locked by them and is left as is.
   * Resolves false (panel should not open) when the lock cannot be taken; the reason is shown as a toast.
   */
  async function beginOrderEdit(order: Order): Promise<boolean> {
    if (selected[order.id]) return true;
    try {
      await callLockOrder(order.sm_number);
      editLocksRef.current.add(order.sm_number);
      return true;
    } catch {
      return false;
    }
  }

  /** Edit side panel closed (saved or cancelled): release the lock taken for it; a selected order keeps its lock. */
  function endOrderEdit(order: Order) {
    if (!editLocksRef.current.delete(order.sm_number)) return;
    clearLockTimer(order.sm_number);
    void unlockOrderQuietly(order.sm_number);
  }

  /** Unselects and unlocks orders, as when they are cleared from the selection. */
  async function releaseOrders(smNumbers: string[]) {
    const release = new Set(smNumbers);
    const ids = orders.filter(o => release.has(o.sm_number)).map(o => o.id);
    setSelected(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
    unlockingRef.current = true;
    smNumbers.forEach(smNumber => clearLockTimer(smNumber));
    await Promise.all(smNumbers.map(unlockOrderQuietly));
    unlockingRef.current = false;
    if (!mountedRef.current) return;
    setOrders(prev => prev.map(o => (release.has(o.sm_number) ? { ...o, isBlocked: false, isBlockedBy: '' } : o)));
  }

  /**
   * Bulk panel: applies one window to the selected orders. Resolves true when the panel can close.
   * Updated orders are then released (unselected and unlocked); skipped ones stay selected for a retry.
   */
  async function applyBulkWindow(selectedOrders: Order[], window: WindowMinutes): Promise<boolean> {
    try {
      configureMsalInstance(msalInstance);
      const result = await bulkUpdateOrderWindow(selectedOrders.map(o => o.sm_number), window);

      const updated = new Set(result.updated);
      const stored = toStoredWindow(result.window, window);
      setOrders(prev => prev.map(o => (updated.has(o.sm_number) ? { ...o, preferred_window: stored } : o)));
      if (result.updated.length > 0) await releaseOrders(result.updated);

      const range = formatWindowRange(stored);
      if (result.skipped.length === 0) {
        showToast(`Delivery window updated for ${result.updated.length} orders · ${range}`, "success", 3000);
      } else {
        const skippedText = result.skipped
          .map(s => `${s.orderId} (${describeSkipReason(s.reason)})`)
          .join(', ');
        showToast(
          `Updated ${result.updated.length} of ${selectedOrders.length} orders · ${range}. Not updated: ${skippedText}`,
          result.updated.length > 0 ? "warning" : "error",
          8000
        );
      }
      return result.updated.length > 0;
    } catch (err) {
      showToast(errorMessage(err, "Failed to update delivery window"), "error", 5000);
      return false;
    }
  }

  const totalPages = pagination.totalPages;
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const displayCount = `${pagination.total} total orders`;

  function getPageNumbers(): Array<number | '…'> {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }

  function navigatePage(newPage: number) {
    if (newPage === page) return;
    setPage(newPage);
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--app-nav-header-height) + var(--app-nav-height))',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--neutral-gray)',
        padding: '8px 24px 8px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      <div className="page-header" style={{ flexShrink: 0, marginBottom: 8 }}>
        <h1 className="page-title">📞 Auto Calls</h1>
        <p className="page-subtitle">Select orders and start calling in Transfer Queue</p>
      </div>

      {loading && orders.length === 0 && (
        <CenteredLoading />
      )}

      {error && !loading && (
        <ErrorState message={error} onRetry={() => load(page, pageSize)} />
      )}

      {/* Step 1: Select — table fills remaining space and scrolls internally */}
      {!(loading && orders.length === 0) && !error && step === 1 && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Step1Select
            filteredOrders={displayedOrders}
            loading={loading}
            dateFilter={dateFilter}
            todayCount={todayOrders.length}
            futureCount={futureOrders.length}
            totalCount={filteredOrders.length}
            selected={selected}
            selectedIds={visibleSelectedIds}
            todayKey={todayKey}
            allSelected={allSelected}
            orderFilterValues={orderFilterValues}
            orderFilters={orderFilters}
            currentUserId={currentUserId}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            columnFilters={columnFilters}
            filtersContextual={filtersContextual}
            onColumnFilterChange={handleColumnFilterChange}
            onFilterSelectOnly={selectOrderFilterOnly}
            onFilterClear={clearOrderFilter}
            onToggle={toggle}
            onSaveOrderEdit={saveOrderEdits}
            onSaveOrderWindow={saveOrderWindow}
            onApplyBulkWindow={applyBulkWindow}
            onBeginOrderEdit={beginOrderEdit}
            onEndOrderEdit={endOrderEdit}
            onToggleAll={toggleAll}
            onSelectToday={() => toggleDateFilter('today')}
            onSelectFuture={() => toggleDateFilter('future')}
            onCreateAndStart={createAndStart}
            emptyAction={
              Object.values(orderFilters).some(s => s && s.size > 0) ? (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    clearAllOrderFilters();
                    setToast({ message: 'Filters cleared', type: 'success' });
                    setTimeout(() => setToast(null), 2000);
                  }}
                >
                  Clear Filters
                </button>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Pinned pagination bar */}
      {!(loading && orders.length === 0) && !error && step === 1 && (
        <div
          className="autocall-pagination"
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            padding: '10px 0',
            borderTop: '1px solid var(--border)',
            background: 'var(--neutral-gray)',
          }}
        >
          <span style={{ fontWeight: 500 }}>Orders per page:</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <button
                key={size}
                type="button"
                className={pageSize === size ? 'primary' : 'secondary'}
                onClick={() => { setPageSize(size); navigatePage(1); }}
                style={{ minWidth: 40 }}
              >
                {size}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <button
              type="button"
              className="secondary"
              onClick={() => navigatePage(Math.max(1, page - 1))}
              disabled={!canPrev}
              style={{ minWidth: 36 }}
            >
              ‹
            </button>
            {getPageNumbers().map((pg, i, arr) =>
              pg === '…' ? (
                <span key={`ellipsis-${arr[i - 1]}-${arr[i + 1]}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
              ) : (
                <button
                  key={pg}
                  type="button"
                  className={page === pg ? 'primary' : 'secondary'}
                  onClick={() => navigatePage(pg)}
                  style={{ minWidth: 36 }}
                >
                  {pg}
                </button>
              )
            )}
            <button
              type="button"
              className="secondary"
              onClick={() => navigatePage(Math.min(totalPages, page + 1))}
              disabled={!canNext}
              style={{ minWidth: 36 }}
            >
              ›
            </button>
          </div>

          <span style={{ fontSize: 13, color: 'var(--text-dark)', whiteSpace: 'nowrap', fontWeight: 700 }}>
            {displayCount}
          </span>

          <button
            type="button"
            className="secondary"
            onClick={() => load(page, pageSize)}
            aria-label="Refresh"
          >
            Refresh
          </button>
        </div>
      )}

      <ToastComponent toast={toast} />
    </div>
  );
}
